import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Container, 
  Typography, 
  CircularProgress, 
  Alert, 
  Paper, 
  Chip, 
  LinearProgress, 
  Button, 
  IconButton, 
  Grid,
  Divider
} from '@mui/material';
import { 
  DateRange as CalendarIcon, 
  CheckCircle as CheckCircleIcon, 
  Schedule as ScheduleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { api } from '../../services/api';
import { useConfirm, useToast } from '../../contexts/ConfirmContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`student-tabpanel-${index}`}
      aria-labelledby={`student-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `student-tab-${index}`,
    'aria-controls': `student-tabpanel-${index}`,
  };
}
interface Topic {
  id: string;
  topicName: string;
  covered: boolean;
  rating: number;
}

interface Lesson {
  id: string;
  lessonDate: string;
  duration: number;
  instructorName: string;
  notes?: string;
  completed?: boolean;
  isApproved?: boolean;
  topicId?: number;
  topicName?: string;
  topicCode?: string;
  topics?: Topic[];
}

const StudentDashboard: React.FC = () => {
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upcomingLessons, setUpcomingLessons] = useState<Lesson[]>([]);
  const [lessonHistory, setLessonHistory] = useState<Lesson[]>([]);
  const [progress, setProgress] = useState<any | null>(null);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const showToast = useToast();

  // Availability browser states
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const fetchAvailableSlots = async (date: Date) => {
    try {
      setLoadingSlots(true);
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await api.get(`/students/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data);
    } catch (err) {
      console.error("Error loading availabilities:", err);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (value === 2) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, value]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch upcoming lessons
      const upcomingResponse = await api.get('/students/lessons?upcoming=true');
      setUpcomingLessons(upcomingResponse.data);
      
      // Fetch lesson history
      const historyResponse = await api.get('/students/lessons');
      setLessonHistory(historyResponse.data);

      // Fetch lessons progress
      const progressResponse = await api.getStudentLessonsProgress();
      setProgress(progressResponse.data);
      
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred while loading data');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancelLesson = async (lessonId: string) => {
    const isConfirmed = await confirm({
      title: 'Cancel Reservation',
      message: 'Are you sure you want to cancel this lesson reservation request?',
      severity: 'warning'
    });
    if (!isConfirmed) return;
    
    try {
      setLoading(true);
      await api.cancelLesson(Number(lessonId));
      showToast('Reservation cancelled successfully.', 'success');
      await fetchData();
      if (value === 2) {
        await fetchAvailableSlots(selectedDate);
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to cancel reservation.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getLessonsForSelectedDate = () => {
    return upcomingLessons.filter(lesson => {
      const lessonDate = parseISO(lesson.lessonDate);
      return format(lessonDate, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
    });
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleSelectSlot = (slot: any) => {
    navigate(`/student/book-lesson?date=${slot.startTime}`);
  };

  const getTheoryTestExpiryCountdown = () => {
    if (!progress?.theoryTestPassDate) return null;
    const passDate = new Date(progress.theoryTestPassDate);
    const expiryDate = new Date(passDate);
    expiryDate.setFullYear(passDate.getFullYear() + 2); // Valid for 2 years
    const diffTime = expiryDate.getTime() - new Date().getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return {
      expiryDate,
      daysLeft: diffDays,
      isExpired: diffDays <= 0
    };
  };

  const theoryCountdown = getTheoryTestExpiryCountdown();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
        Student Dashboard
      </Typography>

      {/* License Upload / Approval / Theory Exam Warnings */}
      {progress && (
        <Box sx={{ mb: 3 }}>
          {/* Rejection Alert */}
          {progress.licenseStatus === 'Rejected' && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} action={
              <Button color="inherit" size="small" onClick={() => navigate('/student/profile')}>
                Update License
              </Button>
            }>
              Your driving license has been rejected by your instructor! Reason: <strong>{progress.licenseRejectionReason || 'Not specified'}</strong>. Please upload new license images in your profile to book a lesson.
            </Alert>
          )}

          {/* Missing License Alert */}
          {(progress.licenseStatus === 'None' || !progress.licenseStatus) && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }} action={
              <Button color="inherit" size="small" onClick={() => navigate('/student/profile')}>
                Upload License
              </Button>
            }>
              To book a lesson, you must first upload the front and back images of your driving license.
            </Alert>
          )}

          {/* Pending Approval Alert */}
          {progress.licenseStatus === 'Pending' && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Your driving license files have been uploaded successfully. Awaiting verification and approval from your instructor.
            </Alert>
          )}

          {/* Theory Expiry Alert */}
          {theoryCountdown && (
            <>
              {theoryCountdown.isExpired ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  Your theory test has expired (2 years)! You may need to retake the theory test to take the practical driving test (Expiry date: {theoryCountdown.expiryDate.toLocaleDateString('en-GB')}).
                </Alert>
              ) : theoryCountdown.daysLeft <= 90 ? (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  <strong>Critical Warning:</strong> There are only <strong>{theoryCountdown.daysLeft} days</strong> left before your theory test expires! You must pass the practical driving test before this date. Expiry date: {theoryCountdown.expiryDate.toLocaleDateString('en-GB')}.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  Theory test validity: Your deadline to complete the practical test is <strong>{theoryCountdown.expiryDate.toLocaleDateString('en-GB')}</strong> ({theoryCountdown.daysLeft} days remaining).
                </Alert>
              )}
            </>
          )}
        </Box>
      )}

      {progress && (
        <Paper 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 3, 
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
            color: '#fff',
            boxShadow: '0 8px 24px rgba(30, 60, 114, 0.15)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              🇬🇧 UK DVSA Syllabus Progress
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 2 }}>
              DVSA syllabus topics completed out of the 19 core topics defined for driving test preparation.
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={((progress.coveredDvsaTopicsCount || 0) / 19) * 100} 
                  sx={{ 
                    height: 10, 
                    borderRadius: 5, 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#4caf50'
                    }
                  }} 
                />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {Math.round(((progress.coveredDvsaTopicsCount || 0) / 19) * 100)}%
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderRadius: 3,
              p: 2,
              minWidth: { xs: '100%', md: 180 },
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
              {progress.coveredDvsaTopicsCount || 0} / 19
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 'medium' }}>
              Topics Completed
            </Typography>
          </Box>
        </Paper>
      )}
      
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="student dashboard tabs" variant="fullWidth">
            <Tab label="Upcoming Lessons" icon={<ScheduleIcon />} iconPosition="start" {...a11yProps(0)} />
            <Tab label="Lesson History" icon={<CheckCircleIcon />} iconPosition="start" {...a11yProps(1)} />
            <Tab label="Book New Lesson" icon={<CalendarIcon />} iconPosition="start" {...a11yProps(2)} />
          </Tabs>
        </Box>
        
        <TabPanel value={value} index={0}>
          {/* Upcoming Lessons Tab */}
          {upcomingLessons.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography color="textSecondary">You have no upcoming or pending scheduled lessons.</Typography>
            </Paper>
          ) : (
            <Box>
              {upcomingLessons.map((lesson) => (
                <Paper key={lesson.id} sx={{ mb: 2, p: 3, borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                      {format(parseISO(lesson.lessonDate), 'dd MMMM yyyy - HH:mm')}
                    </Typography>
                    <Chip 
                      label={lesson.isApproved ? "Approved" : "Pending Approval"} 
                      color={lesson.isApproved ? "success" : "warning"}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Duration:</strong> {lesson.duration} minutes
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Instructor:</strong> {lesson.instructorName}
                    </Typography>
                    {lesson.topicName && (
                      <Box mt={0.5} display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="textSecondary"><strong>Topic:</strong></Typography>
                        <Chip 
                          label={`${lesson.topicCode || ''} - ${lesson.topicName}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ fontWeight: '600' }}
                        />
                      </Box>
                    )}
                    {lesson.notes && (
                      <Typography variant="body2" color="textSecondary">
                        <strong>Notes:</strong> {lesson.notes}
                      </Typography>
                    )}
                  </Box>
                  <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small"
                      onClick={() => handleCancelLesson(lesson.id)}
                      sx={{ fontWeight: 'bold', borderRadius: 2 }}
                    >
                      Cancel / Delete Request
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={value} index={1}>
          {/* Lesson History Tab */}
          {lessonHistory.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography color="textSecondary">You have no lesson history.</Typography>
            </Paper>
          ) : (
            <Box>
              {lessonHistory.map((lesson) => (
                <Paper key={lesson.id} sx={{ mb: 2, p: 3, borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {format(parseISO(lesson.lessonDate), 'dd MMMM yyyy')}
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip 
                        label={lesson.completed ? "Completed" : "Not Completed"} 
                        color={lesson.completed ? "success" : "default"}
                        variant={lesson.completed ? "filled" : "outlined"}
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                      {!lesson.isApproved && (
                        <Chip 
                          label="Rejected/Invalid" 
                          color="error"
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box mt={1.5} display="flex" flexDirection="column" gap={0.5}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Duration:</strong> {lesson.duration} minutes
                    </Typography>
                    {lesson.topicName && (
                      <Box mt={0.5} display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="textSecondary"><strong>Topic:</strong></Typography>
                        <Chip 
                          label={`${lesson.topicCode || ''} - ${lesson.topicName}`} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ fontWeight: '600' }}
                        />
                      </Box>
                    )}
                    {lesson.notes && (
                      <Typography variant="body2" color="textSecondary">
                        <strong>Instructor Notes:</strong> {lesson.notes}
                      </Typography>
                    )}
                  </Box>
                  {lesson.topics && lesson.topics.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Topics Covered:</Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {lesson.topics.map((topic: any) => (
                          <Chip 
                            key={topic.id}
                            label={`${topic.topicName} ${topic.covered ? '✓' : '✗'}`}
                            color={topic.covered ? "primary" : "default"}
                            variant="outlined"
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={value} index={2}>
          {/* Book a Lesson Tab */}
          <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.03)' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, color: '#1a237e' }}>
              Your Instructor's Available Slots
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Select the date you want to book a lesson using the forward/backward arrows, then click on one of the available times below.
            </Typography>

            <Box display="flex" justifyContent="center" alignItems="center" gap={2} sx={{ mb: 4, mt: 2 }}>
              <IconButton 
                onClick={() => {
                  const prev = new Date(selectedDate);
                  prev.setDate(prev.getDate() - 1);
                  setSelectedDate(prev);
                }}
                disabled={format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')}
              >
                <ChevronLeftIcon />
              </IconButton>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={selectedDate}
                  onChange={(date) => date && setSelectedDate(date)}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: 220 }
                    }
                  }}
                />
              </LocalizationProvider>
              <IconButton 
                onClick={() => {
                  const next = new Date(selectedDate);
                  next.setDate(next.getDate() + 1);
                  setSelectedDate(next);
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {loadingSlots ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                {/* Booked lessons for selected date */}
                {(() => {
                  const dayLessons = getLessonsForSelectedDate();
                  if (dayLessons.length === 0) return null;
                  return (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" /> Your Reservations for Today
                      </Typography>
                      <Grid container spacing={2}>
                        {dayLessons.map((lesson) => (
                          <Grid size={{ xs: 12 }} key={lesson.id}>
                            <Paper 
                              sx={{ 
                                p: 2.5, 
                                borderRadius: 3, 
                                border: '2px solid #2e7d32', 
                                backgroundColor: '#f1f8e9',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 2
                              }}
                            >
                              <Box>
                                <Typography variant="h6" fontWeight="bold" color="#2e7d32">
                                  {format(parseISO(lesson.lessonDate), 'HH:mm')} - {format(new Date(parseISO(lesson.lessonDate).getTime() + lesson.duration * 60000), 'HH:mm')}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                  <strong>Status:</strong> {lesson.isApproved ? "Approved" : "Pending Approval"} | <strong>Instructor:</strong> {lesson.instructorName}
                                </Typography>
                                {lesson.notes && (
                                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                    <strong>Your Notes:</strong> {lesson.notes}
                                  </Typography>
                                )}
                              </Box>
                              <Button 
                                variant="contained" 
                                color="error" 
                                size="small"
                                onClick={() => handleCancelLesson(lesson.id)}
                                sx={{ fontWeight: 'bold', borderRadius: 2 }}
                              >
                                Cancel / Delete Request
                              </Button>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                      <Divider sx={{ my: 4 }} />
                    </Box>
                  );
                })()}

                {availableSlots.length === 0 ? (
                  <Alert severity="info" sx={{ py: 2, borderRadius: 2 }}>
                    No available slots found for the selected date ({format(selectedDate, 'dd MMMM yyyy')}). Please check other days.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {availableSlots.map((slot) => {
                      const start = new Date(slot.startTime);
                      const end = new Date(slot.endTime);
                      return (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={slot.id}>
                          <Paper 
                            onClick={() => handleSelectSlot(slot)}
                            sx={{ 
                              p: 3, 
                              textAlign: 'center', 
                              cursor: 'pointer',
                              borderRadius: 3,
                              border: '1px solid #e0e0e0',
                              backgroundColor: '#fff',
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: '#1976d2',
                                backgroundColor: 'rgba(25, 118, 210, 0.02)',
                                boxShadow: '0 6px 18px rgba(0,0,0,0.06)'
                              }
                            }}
                          >
                            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                              {format(start, 'HH:mm')} - {format(end, 'HH:mm')}
                            </Typography>
                            <Chip 
                              label={`${slot.duration || 60} Mins`} 
                              size="small" 
                              color="primary" 
                              variant="outlined" 
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            )}
          </Paper>
        </TabPanel>
      </Box>
    </Container>
  );
};

export default StudentDashboard;
